import React, { useEffect, useMemo, useState } from 'react';
import styles from './MyTeamPage.module.css';
import Flag from './Flag';
import {
  IoChevronDown,
  IoFlagOutline,
  IoLockClosed,
  IoPeople,
  IoShareSocial,
  IoTrophyOutline,
} from 'react-icons/io5';
import ShareCard from './ShareCard';
import StatusIcon from './StatusIcon';

const LOCKED_VALUE = { locked: true };
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

function StadiumMark() {
  return (
    <span className={styles.stadiumMark}>
      <span />
    </span>
  );
}


function LockedValue({ label = 'Locked until match is played' }) {
  return (
    <span className={styles.lockedValue} aria-label={label} title={label}>
      <IoLockClosed />
    </span>
  );
}

function OutcomeIcon({ status, className = '' }) {
  return (
    <span className={`${styles.outcomeIcon} ${className}`}>
      {status === 'locked' ? <LockedValue label="Qualification has not been decided" /> : <StatusIcon status={status} />}
    </span>
  );
}

function getOrdinalSuffix(n) {
  if (n === '-' || n === null || n === undefined) return '';
  const num = Number(n);
  if (Number.isNaN(num)) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function MyTeamPage() {
  const [selectedTeam, setSelectedTeam] = useState('England');
  const [isTeamMenuOpen, setIsTeamMenuOpen] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [nationalTeams, setNationalTeams] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch team list once
  useEffect(() => {
    fetch(`${API_BASE_URL}/teams`)
      .then(r => r.json())
      .then(teams => {
        const sorted = teams.map(t => t.name).sort((a, b) => a.localeCompare(b));
        setNationalTeams(sorted);
      })
      .catch(err => console.error('Failed to load teams:', err));
  }, []);

  // Fetch selected team's group data, refresh every 30s
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/teams/${encodeURIComponent(selectedTeam)}/group`);
        const data = await res.json();
        if (mounted) { setTeamData(data); setLoading(false); }
      } catch (err) {
        console.error('Failed to load team data:', err);
        if (mounted) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [selectedTeam]);

  const groupMatches = teamData?.groupMatches ?? [];
  const knockoutMatches = teamData?.knockoutMatches ?? [];
  const qualificationStatus = teamData?.qualificationStatus ?? 'locked';
  const teamPosition = teamData?.position ?? '-';
  const qualified = teamData?.qualified ?? false;

  const checkpointStatus = qualificationStatus;

  // Find a knockout match by stage name, return its outcome or 'locked'
  function knockoutOutcome(stage) {
    const m = knockoutMatches.find(k => k.stage === stage);
    if (!m) return 'locked';
    return m.outcome === 'scheduled' ? 'locked' : m.outcome;
  }

  const resultSections = useMemo(() => {
    const allCompleted = groupMatches.every(m => m.outcome !== 'scheduled');
    const qualificationDecided = qualificationStatus !== 'locked';

    const knockoutSection = (stage) => {
      const m = knockoutMatches.find(k => k.stage === stage);
      const hasResult = m && m.myScore !== null;
      return {
        title: stage,
        status: knockoutOutcome(stage),
        rows: [
          ['Opponent', m ? m.opponent : LOCKED_VALUE],
          ['Date', m ? m.date : LOCKED_VALUE],
          ['Result', hasResult ? `${m.myScore} - ${m.oppScore}` : LOCKED_VALUE],
        ],
        locked: !hasResult,
      };
    };

    return [
      {
        title: 'Qualification Checkpoint',
        rows: [
          ['Position in Group', allCompleted ? `${teamPosition}${getOrdinalSuffix(teamPosition)}` : LOCKED_VALUE],
          ['Qualified', qualificationDecided ? (qualified ? 'Yes' : 'No') : LOCKED_VALUE],
        ],
        qualified: true,
        status: qualificationStatus,
        locked: !qualificationDecided,
      },
      knockoutSection('Round of 32'),
      knockoutSection('Round of 16'),
      knockoutSection('Quarter-final'),
      knockoutSection('Semi-final'),
      knockoutSection('Final'),
      knockoutSection('Bronze final'),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMatches, knockoutMatches, qualificationStatus, teamPosition, qualified]);

  const knockoutStageNames = ['Round of 32', 'Round of 16', 'Quarter-final', 'Semi-final', 'Final', 'Bronze final'];

  const handleSelectTeam = (team) => {
    setSelectedTeam(team);
    setIsTeamMenuOpen(false);
  };

  return (
    <>
      <section className={styles.myTeamPage}>
        <div className={styles.leftColumn}>
          <div className={styles.heroPanel}>
            <div>
              <p>Team Journey</p>
              <h2>Road to the Trophy</h2>
              <span>Follow your team&apos;s path in the World Cup</span>
            </div>
            <div className={styles.heroActions}>
              <button
                type="button"
                className={styles.shareBtn}
                onClick={() => setShowShareCard(true)}
                aria-label="Share"
              >
                <IoShareSocial />
                <span>Share</span>
              </button>
              <IoTrophyOutline className={styles.heroTrophy} />
            </div>
          </div>

          <div className={styles.journeyPanel}>
            <div className={styles.groupIntro}>
              <span className={styles.peopleIcon}>
                <IoPeople />
              </span>
              <div>
                <h3>Stages</h3>
                <p>Top 2 or Best 8 Third-Place Teams advance to Round of 32</p>
              </div>
            </div>

            <div className={styles.timeline}>
              <div className={styles.timelineRail} />
              <div className={styles.matchStack}>
                {groupMatches.map((match) => (
                  <div key={match.id} className={styles.matchRow}>
                    <StadiumMark />
                    <span className={styles.stepNumber}>{match.id}</span>
                    <div className={styles.matchInfo}>
                      <h3>Group Match {match.id}</h3>
                      <p>vs {match.opponent}</p>
                      <span>{match.venue}</span>
                      <span className={styles.matchDate}>{match.date} {match.kickoffTime} ET</span>
                    </div>
                    <OutcomeIcon status={match.outcome === 'scheduled' ? 'locked' : match.outcome} />
                  </div>
                ))}
              </div>

              <div className={styles.checkpoint}>
                <span className={`${styles.flagNode} ${styles[`flagNode${checkpointStatus.charAt(0).toUpperCase()}${checkpointStatus.slice(1)}`]}`}>
                  <Flag country={selectedTeam} compact className={styles.checkpointFlag} />
                </span>
                <div className={styles.checkpointCard}>
                  <IoFlagOutline />
                  <div>
                    <h3>Qualification Checkpoint</h3>
                    <p>Top 2 or Best 8 Third-Place Teams</p>
                    <p>Advance to Round of 32</p>
                  </div>
                  <OutcomeIcon status={checkpointStatus} />
                </div>
              </div>

              <div className={styles.lockedStack}>
                {knockoutStageNames.map((stage) => {
                  const m = knockoutMatches.find(k => k.stage === stage);
                  const outcome = knockoutOutcome(stage);
                  return (
                    <div key={stage} className={styles.lockedStage}>
                      <span className={styles.stageIcon}><IoTrophyOutline /></span>
                      <div>
                        <h3>{stage}</h3>
                        {m ? <p>vs {m.opponent}</p> : <p>1 Match</p>}
                      </div>
                      <OutcomeIcon status={outcome} />
                    </div>
                  );
                })}
              </div>

            </div>

            <div className={styles.legend}>
              <span><StatusIcon status="advanced" /> Won</span>
              <span><StatusIcon status="eliminated" /> Lost</span>
              <span><StatusIcon status="current" /> Drawn</span>
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.selectedPanel}>
            <h3>Selected Team</h3>
            <div className={styles.teamPicker}>
              <div className={styles.teamCard}>
                <button
                  type="button"
                  className={styles.flagButton}
                  onClick={() => setIsTeamMenuOpen((isOpen) => !isOpen)}
                  aria-expanded={isTeamMenuOpen}
                  aria-haspopup="listbox"
                  aria-label="Choose national team"
                >
                  <Flag country={selectedTeam} compact className={styles.teamFlag} />
                  <IoChevronDown />
                </button>
                <div className={styles.teamDetails}>
                  <strong>{selectedTeam}</strong>
                  <span>{selectedTeam}</span>
                </div>
              </div>
              {isTeamMenuOpen && (
                <div className={styles.teamMenu} role="listbox" aria-label="National teams">
                  {nationalTeams.map((team) => (
                    <button
                      type="button"
                      key={team}
                      className={team === selectedTeam ? styles.selectedTeamOption : ''}
                      onClick={() => handleSelectTeam(team)}
                      role="option"
                      aria-selected={team === selectedTeam}
                      aria-label={team}
                    >
                      <Flag country={team} compact className={styles.optionFlag} />
                      <span>{team}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.resultsPanel}>
            <h2>Match Results</h2>
            <div className={styles.resultCard}>
              <h3>Group Stage</h3>
              {loading && <p style={{ opacity: 0.5 }}>Loading...</p>}
              {groupMatches.map((match) => (
                <div key={match.id} className={styles.resultRow}>
                  <div>
                    <span>Match {match.id}</span>
                    <strong>vs {match.opponent}</strong>
                    <span className={styles.matchDateSmall}>{match.date}</span>
                  </div>
                  <b className={match.outcome === 'current' ? styles.drawScore : match.outcome === 'eliminated' ? styles.loseScore : styles.winScore}>
                    {match.outcome === 'scheduled' ? <LockedValue /> : `${match.myScore} - ${match.oppScore}`}
                  </b>
                  {match.outcome === 'scheduled' ? <LockedValue /> : <StatusIcon status={match.outcome} />}
                </div>
              ))}
            </div>

            {resultSections.map((section) => (
              <div key={section.title} className={styles.resultCard}>
                <h3>{section.title}<span>{section.status ? <OutcomeIcon status={section.status} /> : section.locked ? <LockedValue /> : null}</span></h3>
                {section.rows.map(([label, value]) => (
                  <div key={label} className={styles.detailRow}>
                    <span>{label}</span>
                    <strong>
                      {value === LOCKED_VALUE ? (
                        <LockedValue />
                      ) : section.qualified && label === 'Qualified' ? (
                        <><OutcomeIcon status={section.status} /> {value}</>
                      ) : (
                        value
                      )}
                    </strong>
                  </div>
                ))}
              </div>
            ))}

            <div className={styles.infoNote}>
              <p>The road to the trophy is never just about strength. Every stage is a test of resilience, and champions are those who keep moving forward when the journey gets tough.</p>
            </div>
          </div>
        </div>
      </section>

      {showShareCard && (
        <ShareCard
          selectedTeam={selectedTeam}
          groupMatches={groupMatches.map(m => ({ ...m, status: m.outcome }))}
          knockoutMatches={knockoutMatches}
          checkpointStatus={checkpointStatus}
          teamPosition={teamPosition}
          qualified={qualified}
          onClose={() => setShowShareCard(false)}
        />
      )}
    </>
  );
}

export default MyTeamPage;
